<template>
  <BittsDrawer
    :visible="showDrawer"
    :title="title"
    :primary-button-text="saveButtonText"
    :is-saving="isSaving || isDeleting"
    :is-saving-disabled="
      Boolean(
        isReauthorizing || isDeleting || isConfiguringTray || shouldGateCopilot,
      )
    "
    @action:primary="saveIntegrationSettings"
    @closed="onDrawerClosed"
  >
    <template #headerAvatar>
      <BittsAvatar
        :org="avatarLogo"
        shape="square"
        size="small"
        data-testid="copilot-settings-avatar"
        :is-entity="true"
      />
    </template>

    <template #content>
      <section class="p-16 border-b border-neutral-border">
        <IntegrationCardHeader
          v-if="showStatus"
          :statuses="realOrFakeStatuses"
          :status-type="statusType"
          :callout-text="integrationType === 'gong' ? calloutText : undefined"
          :version
          :created
          :auth-by
          :disabled="!hasPermissionToManageIntegrations"
          :loading="isReauthorizing"
          :hide-reauth="isHubspotCopilot"
          :hide-advanced-settings="
            ['salesforce', 'hubspot'].includes(integrationType)
          "
          @reauthorize="onReauthorize"
        />
        <BittsCallout
          v-else
          action-text="Install"
          type="info"
          :icon="['fak', 'ecosystem-intelligence']"
          title="Make sure this integration is installed"
          subtitle="This integration uses a different system, check the status here"
          @bitts-callout-action="onCalloutClick"
          size="small"
        />
        <BittsCallout
          v-if="showHubspotFeedCallout"
          class="mt-16"
          size="small"
          type="warning"
          title="Your HubSpot data source needs attention"
          subtitle="Copilot in HubSpot reads from this data source. Until it is syncing, partner data in HubSpot will be out of date."
          :action-text="canManageDataSources ? 'View data source' : undefined"
          data-testid="hubspot-feed-attention-callout"
          @bitts-callout-action="goToHubspotFeed"
        />
      </section>
      <section
        v-if="gongInfo && hasPermissionToManageIntegrations"
        class="p-16 border-b border-neutral-border"
      >
        <BittsSwitch
          v-model="isGongActivityEnabled"
          label="Gong Activity ingestion"
          description="Syncs partner mentions from Gong call transcripts to the Crossbeam Activity Timeline."
          :disabled="isSaving"
          data-testid="gong-activity-toggle"
        />
      </section>
      <div v-if="hasPermissionToManageIntegrations" class="copilot_drawer">
        <BittsLoading :is-loading="isLoading">
          <div class="px-16">
            <div class="flex items-center gap-8">
              <h4 class="text-neutral-text-strong font-bold text-base">
                Control what data shows in Copilot
              </h4>
              <BillingCTA
                v-if="isFreeTier || shouldGateCopilot"
                size="x-small"
                data-testid="crossbeam-copilot-settings-cta"
                :billing-interaction="billingCta"
                :route-to="{ name: 'integrations' }"
              />
            </div>
            <p class="text-sm text-neutral-text">
              This will affect all instances of Copilot and not count towards
              your record export limit
            </p>
            <IntegrationSettings
              :population-settings="integrationProfileSettings"
              setting-type="crossbeam_copilot"
              @save:changes="onSettingsUpdated"
              @update:exclude-partnerships-created-since="
                onUpdateExcludePartnershipsCreatedSince
              "
              :is-disabled="Boolean(isFreeTier || shouldGateCopilot)"
            />
          </div>
        </BittsLoading>
      </div>
    </template>
    <template #secondaryButton v-if="gongInfo">
      <BittsButton
        text="Delete integration"
        :left-icon="['fak', 'delete']"
        variant="ghost"
        class="w-1/2"
        type="danger"
        :loading="isDeleting || isReauthorizing || isSaving"
        @click="showDeleteModal = true"
        :disabled="isSaving"
      />
    </template>
  </BittsDrawer>
  <BittsModal
    v-if="showDeleteModal && !!gongInfo"
    variant="confirm"
    title="Delete Integration"
    primary-button-type="danger"
    primary-button-text="Delete"
    description="Are you sure you want to delete the Gong integration?"
    @closed="showDeleteModal = false"
    @action="deleteIntegration(gongInfo)"
  />
</template>
<script setup lang="ts">
import {
  BittsAvatar,
  BittsButton,
  BittsCallout,
  BittsDrawer,
  BittsLoading,
  BittsModal,
  BittsSwitch,
} from '@crossbeam/bitts';
import { EVENT_SITES, INTEGRATIONS_CTAS } from '@crossbeam/itly';

import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

import BillingCTA from '@/components/billing/BillingCTA.vue';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import useHubspotCopilotStatus from '@/composables/useHubspotCopilotStatus';
import useIntegrationProfile from '@/composables/useIntegrationProfile';
import useIntegrations from '@/composables/useIntegrations';
import { useRouterWrapper } from '@/composables/useRouterWrapper';
import useTrayStatus from '@/composables/useTrayStatus';
import { PCLOUD_INTEGRATIONS } from '@/constants/integrations';
import {
  useBillingStore,
  useConnectionsStore,
  useFlashesStore,
  usePartnerCloudStore,
} from '@/stores';
import { EnabledTray, TrayIntegrationHeaderStatus } from '@/types/integrations';

import IntegrationCardHeader from './IntegrationCardHeader.vue';
import IntegrationSettings from './IntegrationSettings.vue';

const showDrawer = ref(true);
const isDeleting = ref(false);
const isReauthorizing = ref(false);
const showDeleteModal = ref(false);
const { pushWithExistingQuery, route, router } = useRouterWrapper();

const partnerCloudStore = usePartnerCloudStore();
const flashesStore = useFlashesStore();
const billingStore = useBillingStore();
const connectionStore = useConnectionsStore();
const { hasPermission } = useAuth();
const { isFreeTier } = storeToRefs(billingStore);

type Integration = 'salesforce' | 'gong' | 'hubspot' | 'outreach' | 'chrome';
const integrationType = computed<Integration>(
  () => route.params.integration as Integration,
);

const {
  clearProfilesState,
  friendlyName,
  hasPermissionToManageIntegrations,
  integrationProfileSettings,
  isLoading,
  isSaving,
  onSettingsUpdated,
  onUpdateExcludePartnershipsCreatedSince,
  saveIntegrationProfile,
} = useIntegrationProfile('crossbeam_copilot');

const showStatus = computed(() =>
  ['gong', 'hubspot', 'salesforce'].includes(integrationType.value),
);
const isSalesforceCopilot = computed(
  () => integrationType.value === 'salesforce',
);

const { isOnFreeTrial } = storeToRefs(billingStore);

const shouldGateCopilot = computed(
  () => isOnFreeTrial.value && isSalesforceCopilot.value,
);

const salesforceConnection = computed(() =>
  connectionStore.getConnectionByType('salesforce'),
);
const version = computed(() => {
  return isSalesforceCopilot.value
    ? salesforceConnection?.value?.config?.app_version
    : undefined;
});

const created = computed(() => {
  if (isHubspotCopilot.value) return hubspotConnectedAt.value;
  return isSalesforceCopilot.value
    ? salesforceConnection?.value?.connected_at
    : undefined;
});

const authBy = computed(() => {
  if (isHubspotCopilot.value) return hubspotConnectedBy.value;
  return isSalesforceCopilot.value
    ? salesforceConnection?.value?.connected_by_user_email
    : undefined;
});

const { allEnabledIntegrations } = useIntegrations();

const gongInfo = computed(() =>
  integrationType.value === 'gong'
    ? allEnabledIntegrations.value.find((i) => i.type === 'tray_gong')
    : undefined,
);

const {
  statusType,
  calloutText,
  reauthorizeTray,
  statuses,
  isConfiguringTray,
} = useTrayStatus(gongInfo);

const isHubspotCopilot = computed(() => integrationType.value === 'hubspot');

const {
  statusType: hubspotStatusType,
  statusText: hubspotStatusText,
  needsFeedAttention,
  feedSettingsRoute,
  connectedAt: hubspotConnectedAt,
  connectedBy: hubspotConnectedBy,
} = useHubspotCopilotStatus();

/* Managing data sources is a permission of its own, so an integrations manager
   without it still gets told what is wrong - just not a link they cannot use. */
const canManageDataSources = computed(() =>
  hasPermission('manage:data-sources'),
);

const showHubspotFeedCallout = computed(
  () => isHubspotCopilot.value && needsFeedAttention.value,
);

function goToHubspotFeed() {
  router.push(feedSettingsRoute.value);
}

const realOrFakeStatuses = computed<TrayIntegrationHeaderStatus[]>(() => {
  if (isHubspotCopilot.value) {
    return [
      {
        type: hubspotStatusType.value,
        text: hubspotStatusText.value,
        name: 'HubSpot',
      },
    ];
  }
  return integrationType.value === 'gong' ? statuses.value : [];
});

const integration = computed(() => route.params.integration);
const billingCta = computed(() => {
  const integrationType =
    typeof integration.value === 'string'
      ? integration.value.toUpperCase()
      : '';
  return {
    cta: INTEGRATIONS_CTAS[
      `COPILOT_FOR_${integrationType}` as keyof typeof INTEGRATIONS_CTAS
    ],
    event_site:
      EVENT_SITES[
        `CROSSBEAM_COPILOT_SETTINGS_${integrationType}` as keyof typeof EVENT_SITES
      ],
  };
});

const basicInfo = computed(() =>
  PCLOUD_INTEGRATIONS.find((i) => {
    const searchType =
      integrationType.value === 'gong' ? 'tray_gong' : integrationType.value;
    return i.type === searchType;
  }),
);

const title = computed(() => `Crossbeam Copilot for ${friendlyName.value}`);

const avatar = computed(() => basicInfo.value?.avatar);
const avatarLogo = computed(() => {
  return avatar.value
    ? {
        logo_url: new URL(
          `../../assets/pngs/partner-cloud/${avatar.value}`,
          import.meta.url,
        ).href,
      }
    : null;
});

const saveButtonText = computed(() => {
  if (isFreeTier.value) return 'Close';
  return isSaving.value ? 'Saving changes' : 'Save changes';
});

function onCalloutClick() {
  window.open(basicInfo.value?.learnMore, '_blank');
}

async function onReauthorize() {
  isReauthorizing.value = true;
  if (isSalesforceCopilot.value) {
    connectionStore.reauthSalesforce();
    isReauthorizing.value = false;
    return;
  }
  if (reauthorizeTray) await reauthorizeTray();
  isReauthorizing.value = false;
}

const isGongActivityEnabled = ref(gongInfo.value?.enabled ?? false);

watch(
  () => gongInfo.value?.enabled,
  (enabled, prevEnabled) => {
    if (prevEnabled === undefined && enabled !== undefined) {
      isGongActivityEnabled.value = enabled;
    }
  },
);

async function saveGongActivityToggle() {
  if (!gongInfo.value) return;
  const newValue = isGongActivityEnabled.value;
  if (newValue === gongInfo.value.enabled) return;
  try {
    const { error } = await crossbeamApi.PUT('/v0.1/tray-integrations', {
      body: {
        instance_id: gongInfo.value.id,
        instance_name: gongInfo.value.name,
        config: { enabled: newValue },
      },
    });
    if (error) throw error;
    await partnerCloudStore.refreshPartnerCloudStore();
  } catch {
    flashesStore.addErrorFlash({
      message: newValue
        ? 'Failed to enable Gong Activity ingestion'
        : 'Failed to disable Gong Activity ingestion',
    });
  }
}

async function saveIntegrationSettings() {
  if (isFreeTier.value) {
    onDrawerClosed();
    return;
  }

  await Promise.all([saveIntegrationProfile(), saveGongActivityToggle()]);
  onDrawerClosed();
}

async function onDrawerClosed() {
  showDrawer.value = false;
  clearProfilesState();
  await pushWithExistingQuery({ name: 'integrations' });
}

const deleteIntegration = async (info: EnabledTray) => {
  try {
    isDeleting.value = true;
    showDeleteModal.value = false;
    await partnerCloudStore.deleteIntegration(info);
  } catch (err) {
    flashesStore.addErrorFlash({
      message: 'Failed to delete integration',
    });
  } finally {
    isDeleting.value = false;
  }
  await pushWithExistingQuery({ name: 'integrations' });
};
</script>

<style lang="pcss">
@reference "../../css/theme.css";
.copilot_drawer {
  @apply py-16;
}
</style>
